import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import {
  Users,
  Package,
  ShoppingBag,
  DollarSign,
  Trash2,
  Edit,
  Plus,
  X,
  Save,
  BarChart3,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "minimal",
    price: 9.99,
    image_url: "",
    tags: "",
  });

  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error("Admin access required");
      navigate("/dashboard");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, designsRes, usersRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/designs`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/orders`),
      ]);
      setStats(statsRes.data);
      setDesigns(designsRes.data);
      setUsers(usersRes.data);
      setOrders(ordersRes.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDesign = async (designId) => {
    if (!window.confirm("Are you sure you want to delete this design?")) return;
    
    try {
      await axios.delete(`${API}/admin/designs/${designId}`);
      setDesigns((prev) => prev.filter((d) => d.id !== designId));
      toast.success("Design deleted");
    } catch (e) {
      toast.error("Failed to delete design");
    }
  };

  const handleEditDesign = (design) => {
    setSelectedDesign(design);
    setFormData({
      title: design.title,
      description: design.description,
      category: design.category,
      price: design.price,
      image_url: design.image_url,
      tags: design.tags?.join(", ") || "",
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const updateData = {
        ...formData,
        price: parseFloat(formData.price),
        tags: formData.tags.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
      };
      
      const response = await axios.put(`${API}/admin/designs/${selectedDesign.id}`, updateData);
      setDesigns((prev) =>
        prev.map((d) => (d.id === selectedDesign.id ? response.data : d))
      );
      setEditModalOpen(false);
      toast.success("Design updated");
    } catch (e) {
      toast.error("Failed to update design");
    }
  };

  const handleCreateDesign = async () => {
    try {
      const createData = {
        ...formData,
        price: parseFloat(formData.price),
        tags: formData.tags.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
      };
      
      const response = await axios.post(`${API}/admin/designs`, createData);
      setDesigns((prev) => [response.data, ...prev]);
      setCreateModalOpen(false);
      setFormData({
        title: "",
        description: "",
        category: "minimal",
        price: 9.99,
        image_url: "",
        tags: "",
      });
      toast.success("Design created");
    } catch (e) {
      toast.error("Failed to create design");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-32 container-padding flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-zinc-200 border-t-indigo-500 rounded-full spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-bg" data-testid="admin-page">
      <Navbar />

      <main className="pt-32 pb-16 container-padding">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <p className="overline mb-2">ADMIN PANEL</p>
            <h1
              className="text-3xl sm:text-4xl font-semibold tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Dashboard
            </h1>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-6 rounded-xl border border-zinc-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-indigo-500" />
                </div>
                <div>
                  <p className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
                    {stats?.total_users || 0}
                  </p>
                  <p className="text-sm text-zinc-500">Total Users</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-zinc-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                  <Package className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
                    {stats?.total_designs || 0}
                  </p>
                  <p className="text-sm text-zinc-500">Total Designs</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-zinc-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                  <ShoppingBag className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
                    {stats?.total_orders || 0}
                  </p>
                  <p className="text-sm text-zinc-500">Total Orders</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-zinc-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
                    ${(stats?.total_revenue || 0).toFixed(2)}
                  </p>
                  <p className="text-sm text-zinc-500">Total Revenue</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start border-b border-zinc-200 bg-transparent h-auto p-0 mb-8">
              <TabsTrigger
                value="overview"
                className="data-[state=active]:border-b-2 data-[state=active]:border-zinc-900 rounded-none bg-transparent px-4 py-3"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Overview
              </TabsTrigger>
              <TabsTrigger
                value="designs"
                className="data-[state=active]:border-b-2 data-[state=active]:border-zinc-900 rounded-none bg-transparent px-4 py-3"
              >
                <Package className="w-4 h-4 mr-2" />
                Designs
              </TabsTrigger>
              <TabsTrigger
                value="orders"
                className="data-[state=active]:border-b-2 data-[state=active]:border-zinc-900 rounded-none bg-transparent px-4 py-3"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Orders
              </TabsTrigger>
              <TabsTrigger
                value="users"
                className="data-[state=active]:border-b-2 data-[state=active]:border-zinc-900 rounded-none bg-transparent px-4 py-3"
              >
                <Users className="w-4 h-4 mr-2" />
                Users
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Category Distribution */}
                <div className="bg-white p-6 rounded-xl border border-zinc-200">
                  <h3 className="font-medium mb-4">Designs by Category</h3>
                  <div className="space-y-3">
                    {stats?.categories?.map((cat) => (
                      <div key={cat._id} className="flex items-center justify-between">
                        <span className="text-sm capitalize">{cat._id || "Unknown"}</span>
                        <span className="text-sm font-medium">{cat.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Orders */}
                <div className="bg-white p-6 rounded-xl border border-zinc-200">
                  <h3 className="font-medium mb-4">Recent Orders</h3>
                  <div className="space-y-3">
                    {stats?.recent_orders?.slice(0, 5).map((order) => (
                      <div key={order.id} className="flex items-center justify-between text-sm">
                        <span className="text-zinc-500">#{order.id.slice(0, 8).toUpperCase()}</span>
                        <span className="font-medium">${order.total?.toFixed(2)}</span>
                      </div>
                    ))}
                    {(!stats?.recent_orders || stats.recent_orders.length === 0) && (
                      <p className="text-sm text-zinc-500">No orders yet</p>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Designs Tab */}
            <TabsContent value="designs">
              <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
                <div className="p-4 border-b border-zinc-200 flex justify-between items-center">
                  <h3 className="font-medium">All Designs ({designs.length})</h3>
                  <button
                    onClick={() => setCreateModalOpen(true)}
                    className="btn-primary text-sm py-2 px-4 flex items-center gap-2"
                    data-testid="create-design-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Add Design
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium text-zinc-500">Design</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-500">Category</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-500">Price</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-500">Created</th>
                        <th className="text-right p-4 text-sm font-medium text-zinc-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {designs.map((design) => (
                        <tr key={design.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <img
                                src={design.image_url}
                                alt={design.title}
                                className="w-12 h-12 rounded-lg object-cover bg-zinc-100"
                              />
                              <div>
                                <p className="font-medium text-sm">{design.title}</p>
                                <p className="text-xs text-zinc-500">
                                  {design.is_ai_generated ? "AI Generated" : "Manual"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="text-sm capitalize">{design.category}</span>
                          </td>
                          <td className="p-4">
                            <span className="text-sm price-tag">${design.price.toFixed(2)}</span>
                          </td>
                          <td className="p-4">
                            <span className="text-sm text-zinc-500">
                              {formatDate(design.created_at)}
                            </span>
                          </td>
                          <td className="p-4 text-right">
                            <button
                              onClick={() => handleEditDesign(design)}
                              className="p-2 text-zinc-400 hover:text-zinc-900 transition-colors"
                              data-testid={`edit-design-${design.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteDesign(design.id)}
                              className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
                              data-testid={`delete-design-${design.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders">
              <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
                <div className="p-4 border-b border-zinc-200">
                  <h3 className="font-medium">All Orders ({orders.length})</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium text-zinc-500">Order ID</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-500">Customer</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-500">Items</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-500">Total</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-500">Status</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-500">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                          <td className="p-4">
                            <span className="text-sm font-mono">
                              #{order.id.slice(0, 8).toUpperCase()}
                            </span>
                          </td>
                          <td className="p-4">
                            <div>
                              <p className="text-sm font-medium">{order.user?.name || "Unknown"}</p>
                              <p className="text-xs text-zinc-500">{order.user?.email}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="text-sm">{order.items?.length || 0} items</span>
                          </td>
                          <td className="p-4">
                            <span className="text-sm price-tag font-medium">
                              ${order.total?.toFixed(2)}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              {order.status}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className="text-sm text-zinc-500">
                              {formatDate(order.created_at)}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {orders.length === 0 && (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-zinc-500">
                            No orders yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
                <div className="p-4 border-b border-zinc-200">
                  <h3 className="font-medium">All Users ({users.length})</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium text-zinc-500">User</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-500">Email</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-500">Role</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-500">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                <span className="text-indigo-600 font-medium">
                                  {u.name?.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <span className="font-medium text-sm">{u.name}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="text-sm text-zinc-600">{u.email}</span>
                          </td>
                          <td className="p-4">
                            {u.is_admin ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                Admin
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-700">
                                User
                              </span>
                            )}
                          </td>
                          <td className="p-4">
                            <span className="text-sm text-zinc-500">
                              {formatDate(u.created_at)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Design</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minimal">Minimal</SelectItem>
                    <SelectItem value="vintage">Vintage</SelectItem>
                    <SelectItem value="streetwear">Streetwear</SelectItem>
                    <SelectItem value="abstract">Abstract</SelectItem>
                    <SelectItem value="nature">Nature</SelectItem>
                    <SelectItem value="pop">Pop Art</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-price">Price ($)</Label>
                <Input
                  id="edit-price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-image">Image URL</Label>
              <Input
                id="edit-image"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-tags">Tags (comma separated)</Label>
              <Input
                id="edit-tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="minimal, mountain, geometric"
              />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setEditModalOpen(false)} className="btn-secondary text-sm py-2 px-4">
              Cancel
            </button>
            <button onClick={handleSaveEdit} className="btn-primary text-sm py-2 px-4 flex items-center gap-2">
              <Save className="w-4 h-4" />
              Save Changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Design</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-title">Title</Label>
              <Input
                id="create-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Design title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-description">Description</Label>
              <Textarea
                id="create-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the design..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minimal">Minimal</SelectItem>
                    <SelectItem value="vintage">Vintage</SelectItem>
                    <SelectItem value="streetwear">Streetwear</SelectItem>
                    <SelectItem value="abstract">Abstract</SelectItem>
                    <SelectItem value="nature">Nature</SelectItem>
                    <SelectItem value="pop">Pop Art</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-price">Price ($)</Label>
                <Input
                  id="create-price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-image">Image URL</Label>
              <Input
                id="create-image"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-tags">Tags (comma separated)</Label>
              <Input
                id="create-tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="minimal, mountain, geometric"
              />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setCreateModalOpen(false)} className="btn-secondary text-sm py-2 px-4">
              Cancel
            </button>
            <button onClick={handleCreateDesign} className="btn-primary text-sm py-2 px-4 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Create Design
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
